/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useCallback, useEffect, lazy, Suspense, use, useRef, useState, type FormEvent } from 'react';
import { useFetcher, useSearchParams } from 'react-router';
import { cn } from '@/lib/utils';
import { useCheckoutContext } from '@/hooks/use-checkout';
import { useBasket, useBasketHydrated } from '@/providers/basket';
import { useCheckoutActions, type PaymentSubmissionRef } from '@/hooks/use-checkout-actions';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Typography } from '@/components/typography';
import { useCustomerProfile } from '@/hooks/checkout/use-customer-profile';
import type { ShopperBasketsV2, ShopperProducts, ShopperPromotions } from '@/scapi';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, AlertCircle } from 'lucide-react';
import { Link } from '@/components/link';
import { routes } from '@/route-paths';
import { useAnalytics } from '@/hooks/use-analytics';
import { UITarget } from '@/targets/ui-target';
import { Spinner } from '@/components/spinner';
import { getCheckoutDisplayError } from './utils/checkout-display-error';
import { CHECKOUT_STEPS, type CheckoutStep } from './utils/checkout-context-types';
import { handlePickupContinueAction, hasAnyValidShippingMethod } from './utils/checkout-utils';
import { isAddressEmpty } from '@/lib/address/address-utils';
import { OrderSummaryMobileAccordion } from '@/components/order-summary/mobile-heading';
// @sfdc-extension-line SFDC_EXT_BOPIS
import { filterDeliveryShippingMethods } from '@/extensions/bopis/lib/basket-utils';

// Lazy load heavy components
const ContactInfo = lazy(() => import('./components/contact-info'));
// @sfdc-extension-line SFDC_EXT_BOPIS
const CheckoutPickupWithData = lazy(() => import('@/extensions/bopis/components/checkout/checkout-pickup-with-data'));
const ShippingAddress = lazy(() => import('./components/shipping-address'));
const ShippingOptions = lazy(() => import('./components/shipping-options'));
const OrderSummary = lazy(() => import('@/components/order-summary'));
const MyCart = lazy(() => import('@/components/my-cart'));

// Import skeleton components for accurate loading states
import {
    CheckoutSkeleton,
    ContactInfoSkeleton,
    MyCartSkeleton,
    OrderSummarySkeleton,
    PickupSkeleton,
    ShippingAddressSkeleton,
    ShippingOptionsSkeleton,
} from './components/checkout-skeletons';

interface CheckoutFormPageProps {
    shippingMethodsMap: Record<string, ShopperBasketsV2.schemas['ShippingMethodResult']>;
    productMapPromise: Promise<Record<string, ShopperProducts.schemas['Product']>>;
    promotionsPromise?: Promise<Record<string, ShopperPromotions.schemas['Promotion']>>;
    showToast?: (message: string, type: 'success' | 'error', options?: { duration?: number }) => void;
    emailVerificationEnabled?: boolean;
}

/**
 * Wrapper component that resolves productMap and promotions Promises within Suspense boundary.
 */
function MyCartWithData({
    basket,
    productMapPromise,
    promotionsPromise,
}: {
    basket: ShopperBasketsV2.schemas['Basket'];
    productMapPromise: Promise<Record<string, ShopperProducts.schemas['Product']>>;
    promotionsPromise?: Promise<Record<string, ShopperPromotions.schemas['Promotion']>>;
}) {
    const productMap = use(productMapPromise);
    const promotions = promotionsPromise ? use(promotionsPromise) : undefined;

    return <MyCart basket={basket} productMap={productMap} promotions={promotions} />;
}

function OrderSummaryWithData({
    basket,
    productMapPromise,
    ...rest
}: {
    basket: ShopperBasketsV2.schemas['Basket'];
    productMapPromise?: Promise<Record<string, ShopperProducts.schemas['Product']>>;
} & Omit<React.ComponentProps<typeof OrderSummary>, 'basket' | 'productsByItemId'>) {
    const productMap = productMapPromise ? use(productMapPromise) : undefined;
    // No Tax row in checkout
    return <OrderSummary basket={basket} productsByItemId={productMap} showTax={false} {...rest} />;
}

export default function CheckoutFormPage({
    shippingMethodsMap: shippingMethodsMapFromLoader,
    productMapPromise,
    promotionsPromise,
    showToast,
    emailVerificationEnabled: _emailVerificationEnabled,
}: CheckoutFormPageProps) {
    const { t } = useTranslation('checkout');
    const { t: tErrors } = useTranslation('errors');
    const tAny = t as (key: string) => string;

    const cart = useBasket();
    const basketHydrated = useBasketHydrated();
    const { step, STEPS, goToStep, pinToStep, editingStep, shipmentDistribution, exitEditMode } = useCheckoutContext();
    const customerProfile = useCustomerProfile();
    const isRegisteredUser = Boolean(customerProfile?.customer?.customerId);
    const registrationFetcher = useFetcher({ key: 'checkout-registration' });
    const isRegistrationInProgress = registrationFetcher.state !== 'idle';

    const paymentSubmissionRef = useRef<PaymentSubmissionRef['current']>({
        formDataGetter: null,
        shouldPlaceOrderAfterPayment: false,
        options: null,
        setFormErrors: null,
    });
    const otpFlowActiveRef = useRef(false);
    const noShippingMethodsRef = useRef(false);
    const [hideCreateAccountAfterSkippedPasswordlessOtp, setHideCreateAccountAfterSkippedPasswordlessOtp] =
        useState(false);

    // Consent checkboxes state
    const [agreedContact, setAgreedContact] = useState(false);
    const [agreedMahindra, setAgreedMahindra] = useState(false);
    const [agreedGroupCompanies, setAgreedGroupCompanies] = useState(false);
    const [consentError, setConsentError] = useState<string | null>(null);

    let paymentError: string | null = null;
    let paymentErrorMessage: string | null = null;
    try {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const [searchParams] = useSearchParams();
        paymentError = searchParams.get('paymentError');
        paymentErrorMessage = searchParams.get('message');
    } catch {
        if (typeof window !== 'undefined') {
            const params = new URLSearchParams(window.location.search);
            paymentError = params.get('paymentError');
            paymentErrorMessage = params.get('message');
        }
    }

    // Checkout actions hook with all fetchers and submission handlers
    const {
        submitContactInfo,
        submitShippingAddress,
        submitShippingOptions,
        submitPlaceOrder,
        contactFetcher,
        shippingAddressFetcher,
        shippingOptionsFetcher,
        placeOrderFetcher,
        isSubmitting,
        handleCreateAccountPreferenceChange,
    } = useCheckoutActions({ paymentSubmissionRef, otpFlowActiveRef, noShippingMethodsRef });

    // Stale session cleanup
    const sessionCleanupDoneRef = useRef(false);
    useEffect(() => {
        if (typeof sessionStorage === 'undefined' || sessionCleanupDoneRef.current) {
            return;
        }
        const hasSavedPaymentMethods = (customerProfile?.paymentInstruments?.length ?? 0) > 0;
        if (!isRegisteredUser || !hasSavedPaymentMethods) {
            return;
        }
        sessionStorage.removeItem('registeredViaCheckout');
        sessionStorage.removeItem('shouldCreateAccount');
        handleCreateAccountPreferenceChange(false);
        sessionCleanupDoneRef.current = true;
    }, [isRegisteredUser, customerProfile?.paymentInstruments?.length, handleCreateAccountPreferenceChange]);

    /**
     * Shopper closed passwordless OTP via "Checkout as guest" — do not verify OTP / sign in.
     * Unblock contact step and hide place-order create-account checkbox for this checkout session.
     */
    const handleRegisteredUserChoseGuest = useCallback(() => {
        setHideCreateAccountAfterSkippedPasswordlessOtp(true);
        otpFlowActiveRef.current = false;
        if (contactFetcher.state === 'idle' && contactFetcher.data?.success === true) {
            exitEditMode();
        }
    }, [contactFetcher.state, contactFetcher.data, exitEditMode]);

    /** After successful OTP verification at contact, restore create-account UI if still applicable */
    const handlePasswordlessOtpVerifiedAtContact = useCallback(() => {
        setHideCreateAccountAfterSkippedPasswordlessOtp(false);
    }, []);

    let showAddressAndOptions = true;

    // Determine shipping methods: prefer action response over loader data (avoids flash when advancing to shipping step)
    const actionShippingMethods = shippingAddressFetcher.data?.data?.shippingMethodsMap as
        | Record<string, ShopperBasketsV2.schemas['ShippingMethodResult']>
        | undefined;
    let shippingMethodsMap =
        actionShippingMethods && Object.keys(actionShippingMethods).length > 0
            ? actionShippingMethods
            : shippingMethodsMapFromLoader;

    // @sfdc-extension-block-start SFDC_EXT_BOPIS
    shippingMethodsMap = filterDeliveryShippingMethods(shippingMethodsMap);
    const hasPickupItems = shipmentDistribution.hasPickupItems;
    showAddressAndOptions = shipmentDistribution.hasDeliveryItems;
    // @sfdc-extension-block-end SFDC_EXT_BOPIS

    // Keep ref in sync so useCheckoutActions can block advance before rendering the next step
    noShippingMethodsRef.current = !hasAnyValidShippingMethod(shippingMethodsMap);

    // @sfdc-extension-block-start SFDC_EXT_BOPIS
    const { t: tBopis } = useTranslation('extBopis');
    const { label: pickupProceedButtonLabel, onClick: onPickupContinueClick } = handlePickupContinueAction(
        hasPickupItems,
        showAddressAndOptions,
        goToStep,
        STEPS,
        tBopis as (key: string) => string
    );
    // @sfdc-extension-block-end SFDC_EXT_BOPIS

    const analytics = useAnalytics();
    const hasTrackedCheckoutStartRef = useRef(false);
    const previousStepRef = useRef<CheckoutStep | null>(null);

    useEffect(() => {
        // Only track checkout start once on mount if basket is not empty
        if (!hasTrackedCheckoutStartRef.current && cart?.productItems && cart.productItems.length > 0) {
            void analytics.trackCheckoutStart({
                basket: cart,
            });
            hasTrackedCheckoutStartRef.current = true;
        }
    }, [analytics, cart]);

    useEffect(() => {
        if (previousStepRef.current !== step && cart?.productItems && cart.productItems.length > 0) {
            const stepName = Object.keys(STEPS).find((key) => STEPS[key as keyof typeof STEPS] === step) || '';
            void analytics.trackCheckoutStep({
                stepName,
                stepNumber: step,
                basket: cart,
            });
            previousStepRef.current = step;
        }
    }, [analytics, step, STEPS, cart]);

    const isPlacingOrder = placeOrderFetcher.state === 'submitting';
    const [isPlaceOrderPending, setIsPlaceOrderPending] = useState(false);
    const [shippingMethodValidationError, setShippingMethodValidationError] = useState<string | null>(null);

    // Form submission handlers - delegated to checkout actions hook
    const handleContactSubmit = submitContactInfo;
    const handleShippingAddressSubmit = submitShippingAddress;
    const handleShippingOptionsSubmit = (formData: FormData) => {
        setShippingMethodValidationError(null);
        submitShippingOptions(formData);
    };

    const handleProceedToPay = (e: FormEvent) => {
        e.preventDefault();

        // Block submission while a section is being edited.
        if (editingStep !== null && editingStep !== STEPS.PLACE_ORDER) {
            return;
        }

        // Validate that all non-empty shipments have a shipping method selected.
        if (cart?.shipments && cart?.productItems) {
            const shipmentItemCounts = new Map<string, number>();
            for (const item of cart.productItems) {
                if (item.shipmentId) {
                    shipmentItemCounts.set(item.shipmentId, (shipmentItemCounts.get(item.shipmentId) || 0) + 1);
                }
            }
            const missingShippingMethod = cart.shipments.some(
                (shipment) =>
                    shipment.shipmentId &&
                    (shipmentItemCounts.get(shipment.shipmentId) || 0) > 0 &&
                    !shipment.shippingMethod
            );
            if (missingShippingMethod) {
                setShippingMethodValidationError(tErrors('checkout.shippingMethodRequired'));
                goToStep(STEPS.SHIPPING_OPTIONS);
                return;
            }
        }

        if (!agreedContact) {
            setConsentError('Please agree to the Terms & Conditions and Privacy Policy to proceed.');
            showToast?.('Please agree to the Terms & Conditions and Privacy Policy to proceed.', 'error');
            return;
        }

        setConsentError(null);
        setIsPlaceOrderPending(true);
        submitPlaceOrder();
    };

    // Step state logic - centralized in container for single page layout
    const contactInfoState = {
        isCompleted: step > STEPS.CONTACT_INFO,
        isEditing: step === STEPS.CONTACT_INFO || editingStep === STEPS.CONTACT_INFO,
        onEdit: () => goToStep(STEPS.CONTACT_INFO),
    };

    const shippingAddressState = {
        isCompleted: step > STEPS.SHIPPING_ADDRESS,
        isEditing: step === STEPS.SHIPPING_ADDRESS || editingStep === STEPS.SHIPPING_ADDRESS,
        onEdit: () => {
            goToStep(STEPS.SHIPPING_ADDRESS);
        },
    };

    const shippingOptionsState = {
        isCompleted: step > STEPS.SHIPPING_OPTIONS,
        isEditing: step === STEPS.SHIPPING_OPTIONS || editingStep === STEPS.SHIPPING_OPTIONS,
        onEdit: () => goToStep(STEPS.SHIPPING_OPTIONS),
    };

    const hasShippingAddress = cart?.shipments?.some((s) => s.shippingAddress && !isAddressEmpty(s.shippingAddress));
    // Every step (contact, address, shipping method) is filled in and none is being edited.
    const allStepsComplete =
        step > STEPS.SHIPPING_OPTIONS && (editingStep === null || editingStep === STEPS.PLACE_ORDER);
    const shippingBlocked = showAddressAndOptions && !!hasShippingAddress && noShippingMethodsRef.current;

    // Surface blocking API errors as error toasts for immediate visibility.
    useEffect(() => {
        if (
            placeOrderFetcher.state === 'idle' &&
            placeOrderFetcher.data &&
            !placeOrderFetcher.data.success &&
            placeOrderFetcher.data.error
        ) {
            setIsPlaceOrderPending(false);
            const error = getCheckoutDisplayError(placeOrderFetcher.data, undefined, tAny);
            if (error) showToast?.(error, 'error');
        }
    }, [placeOrderFetcher.state, placeOrderFetcher.data, showToast, tAny]);

    useEffect(() => {
        if (contactFetcher.state !== 'idle' || !contactFetcher.data || contactFetcher.data.success) return;
        const error = getCheckoutDisplayError(contactFetcher.data, 'contactInfo', tAny);
        if (error) showToast?.(error, 'error');
    }, [contactFetcher.state, contactFetcher.data, showToast, tAny]);

    useEffect(() => {
        if (
            shippingAddressFetcher.state !== 'idle' ||
            !shippingAddressFetcher.data ||
            shippingAddressFetcher.data.success
        )
            return;
        const error = getCheckoutDisplayError(shippingAddressFetcher.data, 'shippingAddress', tAny);
        if (error) showToast?.(error, 'error');
    }, [shippingAddressFetcher.state, shippingAddressFetcher.data, showToast, tAny]);

    useEffect(() => {
        if (
            shippingOptionsFetcher.state !== 'idle' ||
            !shippingOptionsFetcher.data ||
            shippingOptionsFetcher.data.success
        )
            return;
        const error = getCheckoutDisplayError(shippingOptionsFetcher.data, 'shippingOptions', tAny);
        if (error) showToast?.(error, 'error');
    }, [shippingOptionsFetcher.state, shippingOptionsFetcher.data, showToast, tAny]);

    // Reload-only: pin to Shipping Address when basket already has an address but no valid delivery methods.
    const reloadPinDoneRef = useRef(false);
    useEffect(() => {
        if (reloadPinDoneRef.current || !cart || !basketHydrated) return;
        if (shippingAddressFetcher.state !== 'idle' || shippingAddressFetcher.data) return;
        const hasAddress = cart.shipments?.some((s) => s.shippingAddress && !isAddressEmpty(s.shippingAddress));
        if (!hasAddress) return;
        if (!hasAnyValidShippingMethod(shippingMethodsMap)) {
            reloadPinDoneRef.current = true;
            pinToStep?.(STEPS.SHIPPING_ADDRESS);
            showToast?.(tErrors('checkout.noShippingMethodsForAddress'), 'error');
        }
    }, [
        cart,
        basketHydrated,
        shippingMethodsMap,
        shippingAddressFetcher.state,
        shippingAddressFetcher.data,
        pinToStep,
        STEPS,
        showToast,
        tErrors,
    ]);

    const noMethodsToastShownRef = useRef<unknown>(null);
    useEffect(() => {
        if (shippingAddressFetcher.state !== 'idle' || !shippingAddressFetcher.data?.success) return;
        if (noMethodsToastShownRef.current === shippingAddressFetcher.data) return;
        if (!hasAnyValidShippingMethod(shippingMethodsMap)) {
            noMethodsToastShownRef.current = shippingAddressFetcher.data;
            showToast?.(tErrors('checkout.noShippingMethodsForAddress'), 'error');
            pinToStep?.(STEPS.SHIPPING_ADDRESS);
        }
    }, [
        shippingAddressFetcher.state,
        shippingAddressFetcher.data,
        shippingMethodsMap,
        showToast,
        tErrors,
        pinToStep,
        STEPS,
    ]);

    if (!cart || !basketHydrated) {
        return <CheckoutSkeleton />;
    }

   const isLeavingForPayment =
        placeOrderFetcher.state !== 'idle' ||
        placeOrderFetcher.data?.success === true ||
        Boolean(placeOrderFetcher.data?.payuRedirectUrl);
    if (isLeavingForPayment && !cart.productItems?.length) {
        return (
            <div
                className="min-h-screen bg-background flex flex-col items-center justify-center gap-4 p-4"
                role="status"
                aria-live="polite"
                data-testid="checkout-redirecting">
                <Spinner size="lg" />
                <p className="font-lato text-base leading-[22px] text-[#17171B]">
                    {placeOrderFetcher.data?.payuRedirectUrl ? 'Redirecting to payment…' : 'Placing your order…'}
                </p>
            </div>
        );
    }

    if (!cart.basketId || !cart.productItems || cart.productItems.length === 0) {
        return (
            <div className="min-h-screen bg-muted flex items-center justify-center p-4">
                <Card className="w-full max-w-md rounded-lg shadow-sm border border-[#D1D5DB]">
                    <CardContent className="pt-6 text-center space-y-4">
                        {paymentError && (
                            <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-4 text-left text-destructive flex items-start gap-3">
                                <AlertCircle className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
                                <div>
                                    <h4 className="font-semibold text-sm text-destructive">Payment Failed</h4>
                                    <p className="text-sm mt-1 text-destructive/90">
                                        {paymentErrorMessage
                                            ? decodeURIComponent(paymentErrorMessage)
                                            : 'Your previous transaction was unsuccessful.'}
                                    </p>
                                </div>
                            </div>
                        )}
                        <Typography variant="muted" className="text-center">
                            {t('common.emptyCart')}
                        </Typography>
                        <Button asChild variant="outline" className="mt-4">
                            <Link to={routes.home}>Return to Shop</Link>
                        </Button>
                    </CardContent>
                </Card>
            </div>
        );
    }

    const shippingAddressComponent = (
        <ShippingAddress
            onSubmit={handleShippingAddressSubmit}
            isLoading={isSubmitting('shipping-address')}
            actionData={shippingAddressFetcher.data}
            {...shippingAddressState}
        />
    );
    const defaultShipmentId = cart?.shipments?.[0]?.shipmentId ?? 'me';
    const shippingOptionsComponent = (
        <ShippingOptions
            onSubmit={handleShippingOptionsSubmit}
            isLoading={isSubmitting('shipping-options')}
            actionData={shippingOptionsFetcher.data}
            shippingMethods={shippingMethodsMap[defaultShipmentId]}
            validationError={shippingMethodValidationError}
            {...shippingOptionsState}
        />
    );

    // Single delivery address only — multi-address checkout (multiship extension) is intentionally not offered

    const isEstimate = true;
    const totalItems = cart?.productItems?.reduce((acc, item) => acc + (item.quantity ?? 1), 0) || 0;

    return (
        <div className="checkout-page bg-background">
            <UITarget targetId="sfcc.checkout.page.before" />
            {/* On mobile the Proceed to Pay bar is fixed and its own spacer (see _checkout.tsx)
                already reserves clearance below the page content — an extra pb-6 here would just
                stack on top of that and show as dead space below the consent section. */}
            <div className="section-container pt-4 md:pt-6 pb-0 lg:pb-6">
                {/* Back / Breadcrumb Navigation */}
                <nav className="checkout-back-nav flex items-center mb-4" aria-label="Breadcrumb">
                    <Link
                        to={routes.cart}
                        className="checkout-back-link inline-flex items-center gap-1.5 text-sm font-medium text-[#121212] border-b-[1.5px] border-[#121212] pb-0.5 hover:opacity-75 transition-opacity">
                        <ArrowLeft className="w-4 h-4" strokeWidth={1.8} />
                        <span>Back</span>
                    </Link>
                </nav>

                <h1 className="checkout-page-title mb-3 md:mb-6">
                    CHECKOUT ({totalItems} {totalItems === 1 ? 'ITEM' : 'ITEMS'})
                </h1>

                {/* Desktop: 725px form column + 449px sidebar (fr units hit exactly these widths at the full
                    1366px container; they scale down proportionally on narrower desktops) */}
                <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,725fr)_minmax(0,449fr)] gap-8 lg:gap-x-16 items-start">
                    {/* Order Summary Sidebar - above content on md, right side on lg */}
                    <div
                        // lg:mt-6 matches the Contact Information card's top margin so both columns start level
                        className="hidden md:block md:order-1 lg:order-2 lg:col-span-1 lg:mt-6 space-y-6"
                        data-testid="checkout-order-summary-sidebar">
                        <UITarget targetId="sfcc.checkout.sidebar.before" />

                        {/* Card 1: Order Summary */}
                        <Card className="checkout-order-summary-card shadow-none bg-white overflow-hidden py-0 gap-0">
                            <CardHeader className="grid-rows-1 gap-0 border-b border-[#D1D5DB] p-6 pb-4">
                                <CardTitle>
                                    <span className="text-lg font-semibold tracking-tight text-[#3F3F46]">
                                        {t('orderSummary.title')}
                                    </span>
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="px-6 pt-2 pb-3">
                                <UITarget targetId="sfcc.checkout.orderSummary.before" />
                                <UITarget targetId="sfcc.checkout.orderSummary">
                                    <Suspense fallback={<OrderSummarySkeleton />}>
                                        <OrderSummaryWithData
                                            basket={cart}
                                            productMapPromise={productMapPromise}
                                            showCartItems={false}
                                            showHeading={false}
                                            showPromoCodeForm={false}
                                            isEstimate={isEstimate}
                                            className="border-none shadow-none rounded-none !py-0 [&_[data-slot=card-content]]:px-0 [--cart-summary-px:0rem]"
                                        />
                                    </Suspense>
                                </UITarget>
                                <UITarget targetId="sfcc.checkout.orderSummary.after" />
                            </CardContent>
                        </Card>

                        {/* Card 2: X Items */}
                        <Card className="checkout-items-card w-full max-w-[449px] rounded-lg border border-[#D1D5DB] shadow-sm bg-white overflow-hidden py-0 gap-0">
                            <CardHeader className="checkout-items-card-header border-b border-[#D1D5DB]">
                                <CardTitle>
                                    <span className="text-xl font-bold tracking-tight text-[#3F3F46]">
                                        {totalItems} {totalItems === 1 ? 'Item' : 'Items'}
                                    </span>
                                </CardTitle>
                            </CardHeader>
                            {/* Items list: 20px padding, 16px between items; four items fill the 668px height,
                                more than that scroll inside it */}
                            <CardContent className="p-5 max-h-[668px] overflow-y-auto">
                                <UITarget targetId="sfcc.checkout.myCart.before" />
                                <UITarget targetId="sfcc.checkout.myCart">
                                    <Suspense fallback={<MyCartSkeleton itemCount={cart?.productItems?.length || 2} />}>
                                        <MyCartWithData
                                            basket={cart}
                                            productMapPromise={productMapPromise}
                                            promotionsPromise={promotionsPromise}
                                        />
                                    </Suspense>
                                </UITarget>
                                <UITarget targetId="sfcc.checkout.myCart.after" />
                            </CardContent>
                        </Card>

                        <UITarget targetId="sfcc.checkout.sidebar.after" />
                    </div>

                    {/* Main Checkout Content - Single Page Layout */}
                    <div className="space-y-6 order-2 lg:order-1 lg:col-span-1">
                        <UITarget targetId="sfcc.checkout.mainContent.before" />

                        {paymentError && (
                            <div
                                role="alert"
                                className="rounded-lg border border-destructive/30 bg-destructive/10 p-4 text-destructive shadow-sm flex items-start gap-3">
                                <AlertCircle className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
                                <div className="flex-1">
                                    <h4 className="font-semibold text-sm text-destructive">Payment Unsuccessful</h4>
                                    <p className="text-sm mt-1 text-destructive/90">
                                        {paymentErrorMessage
                                            ? decodeURIComponent(paymentErrorMessage)
                                            : 'Your previous payment attempt was unsuccessful. Your items and details have been preserved. Please review and try again.'}
                                    </p>
                                </div>
                            </div>
                        )}

                        <UITarget targetId="sfcc.checkout.contactInfo.header.before" />
                        <Suspense fallback={<ContactInfoSkeleton />}>
                            <UITarget targetId="sfcc.checkout.contactInfo.before" />
                            <UITarget targetId="sfcc.checkout.contactInfo">
                                {!cart ? (
                                    <ContactInfoSkeleton />
                                ) : (
                                    <ContactInfo
                                        onSubmit={handleContactSubmit}
                                        isLoading={isSubmitting('contact')}
                                        actionData={contactFetcher.data}
                                        otpFlowActiveRef={otpFlowActiveRef}
                                        onRegisteredUserChoseGuest={handleRegisteredUserChoseGuest}
                                        onPasswordlessOtpVerified={handlePasswordlessOtpVerifiedAtContact}
                                        suppressRegisteredEmailLoginHints={hideCreateAccountAfterSkippedPasswordlessOtp}
                                        {...contactInfoState}
                                    />
                                )}
                            </UITarget>
                            <UITarget targetId="sfcc.checkout.contactInfo.after" />
                        </Suspense>

                        {/* @sfdc-extension-block-start SFDC_EXT_BOPIS */}
                        {/* Store Pickup Information */}
                        {hasPickupItems && (
                            <Suspense fallback={<PickupSkeleton />}>
                                <CheckoutPickupWithData
                                    cart={cart}
                                    productMapPromise={productMapPromise}
                                    isEditing={editingStep === CHECKOUT_STEPS.PICKUP}
                                    onEdit={() => goToStep(CHECKOUT_STEPS.PICKUP)}
                                    onContinue={onPickupContinueClick}
                                    continueButtonLabel={pickupProceedButtonLabel}
                                />
                            </Suspense>
                        )}
                        {/* @sfdc-extension-block-end SFDC_EXT_BOPIS */}

                        {/* Shipping Address & Options */}
                        {showAddressAndOptions && (
                            <>
                                <UITarget targetId="sfcc.checkout.shippingAddress.header.before" />
                                <Suspense fallback={<ShippingAddressSkeleton />}>
                                    <UITarget targetId="sfcc.checkout.shippingAddress.before" />
                                    <UITarget targetId="sfcc.checkout.shippingAddress">
                                        {shippingAddressComponent}
                                    </UITarget>
                                    <UITarget targetId="sfcc.checkout.shippingAddress.after" />
                                </Suspense>

                                <div>
                                    <UITarget targetId="sfcc.checkout.shippingOptions.header.before" />
                                    <Suspense fallback={<ShippingOptionsSkeleton />}>
                                        <UITarget targetId="sfcc.checkout.shippingOptions.before" />
                                        <UITarget targetId="sfcc.checkout.shippingOptions">
                                            {shippingOptionsComponent}
                                        </UITarget>
                                        <UITarget targetId="sfcc.checkout.shippingOptions.after" />
                                    </Suspense>
                                </div>
                            </>
                        )}
                        <div className="md:hidden mb-6">
                            <Suspense fallback={<OrderSummarySkeleton />}>
                                <OrderSummaryWithData
                                    basket={cart}
                                    productMapPromise={productMapPromise}
                                    showCartItems={false}
                                    showHeading={true}
                                    showPromoCodeForm={false}
                                    isEstimate={isEstimate}
                                    showTotal={true}
                                    showCheckoutAction={false}
                                    className="checkout-order-summary-card !pb-3"
                                />
                            </Suspense>
                        </div>
                        <div className="md:hidden mb-6 checkout-order-summary-card">
                            <Suspense fallback={<OrderSummarySkeleton />}>
                                <OrderSummaryMobileAccordion
                                    basket={cart}
                                    defaultExpanded={false}
                                    showPrice
                                    isEstimate={isEstimate}>
                                    <Suspense
                                        fallback={<MyCartSkeleton itemCount={cart?.productItems?.length || 2} />}>
                                        <MyCartWithData
                                            basket={cart}
                                            productMapPromise={productMapPromise}
                                            promotionsPromise={promotionsPromise}
                                        />
                                    </Suspense>
                                </OrderSummaryMobileAccordion>
                            </Suspense>
                        </div>
                        {/* Mahindra Consent & Direct Proceed to Pay Section */}
                        <div className="pt-2 lg:space-y-5">
                            {/* On mobile the consent block only appears once every step is filled in.
                                The button below is `fixed` on mobile (off-flow), so no flow gap is
                                needed before it there; lg:space-y-5 restores that gap once it's static. */}
                            <div
                                className={cn(
                                    'flex flex-col gap-8 lg:gap-5',
                                    !allStepsComplete && 'hidden'
                                )}>
                            {consentError && (
                                <div className="bg-destructive/10 border border-destructive/20 text-destructive text-sm p-3 rounded">
                                    {consentError}
                                </div>
                            )}

                            {/* Checkbox 1: Mandatory T&C */}
                            <div className="flex items-start gap-3">
                                <Checkbox
                                    id="consent-contact-tc"
                                    checked={agreedContact}
                                    onCheckedChange={(checked) => {
                                        setAgreedContact(checked === true);
                                        if (checked) setConsentError(null);
                                    }}
                                    className="size-5 mt-0.5 rounded border-[#262626] data-[state=checked]:bg-black data-[state=checked]:border-black data-[state=checked]:text-white cursor-pointer shrink-0"
                                />
                                <label
                                    htmlFor="consent-contact-tc"
                                    className="checkout-consent-label text-sm text-[#262626] cursor-pointer select-none">
                                    I agree to be contacted by Mahindra &amp; Mahindra and associates regarding my
                                    interest via phone call, WhatsApp or any other medium. I also agree to
                                    website&apos;s{' '}
                                    <Link
                                        to="/terms"
                                        target="_blank"
                                        className="checkout-consent-link hover:opacity-80">
                                        T&amp;C
                                    </Link>{' '}
                                    and{' '}
                                    <Link
                                        to="/privacy-policy"
                                        target="_blank"
                                        className="checkout-consent-link hover:opacity-80">
                                        Privacy Policy
                                    </Link>
                                    .*
                                </label>
                            </div>

                            {/* Marketing Communications Consent */}
                            <div className="space-y-3">
                                <p className="checkout-consent-text text-sm text-[#262626]">
                                    I consent to receiving marketing and promotional communications via email, WhatsApp,
                                    or other mediums regarding offers, products, and services from:
                                </p>

                                <div className="space-y-2.5">
                                    <div className="flex items-center gap-3">
                                        <Checkbox
                                            id="consent-mahindra-ltd"
                                            checked={agreedMahindra}
                                            onCheckedChange={(checked) => setAgreedMahindra(checked === true)}
                                            className="size-5 rounded border-[#262626] data-[state=checked]:bg-black data-[state=checked]:border-black data-[state=checked]:text-white cursor-pointer shrink-0"
                                        />
                                        <label
                                            htmlFor="consent-mahindra-ltd"
                                            className="checkout-consent-label text-sm text-[#262626] cursor-pointer select-none">
                                            Mahindra &amp; Mahindra Limited
                                        </label>
                                    </div>

                                    <div className="flex items-center gap-3">
                                        <Checkbox
                                            id="consent-group-companies"
                                            checked={agreedGroupCompanies}
                                            onCheckedChange={(checked) => setAgreedGroupCompanies(checked === true)}
                                            className="size-5 rounded border-[#262626] data-[state=checked]:bg-black data-[state=checked]:border-black data-[state=checked]:text-white cursor-pointer shrink-0"
                                        />
                                        <label
                                            htmlFor="consent-group-companies"
                                            className="checkout-consent-label text-sm text-[#262626] cursor-pointer select-none">
                                            Mahindra Group Companies beyond Mahindra &amp; Mahindra Limited
                                        </label>
                                    </div>
                                </div>
                            </div>
                            </div>

                            {/* Proceed to Pay Button - Sticky at bottom on mobile, inline on desktop */}
                            <div
                                data-checkout-mobile-bar
                                className={cn(
                                    'fixed bottom-0 left-0 right-0 z-50 border-t border-[#E5E7EB] bg-white px-4 py-3 pb-[calc(0.75rem+env(safe-area-inset-bottom,0px))] shadow-[0_-4px_12px_rgba(0,0,0,0.06)] lg:static lg:inset-auto lg:z-auto lg:w-full lg:border-0 lg:bg-transparent lg:p-0 lg:pt-2 lg:shadow-none',
                                    !allStepsComplete && 'lg:hidden'
                                )}>
                                <Button
                                    type="button"
                                    onClick={handleProceedToPay}
                                    disabled={
                                        isPlacingOrder ||
                                        isPlaceOrderPending ||
                                        !allStepsComplete ||
                                        !agreedContact ||
                                        shippingBlocked
                                    }
                                    className="checkout-proceed-pay-btn w-full lg:w-[320px] h-[48px] rounded-full bg-[#555555] hover:bg-black text-white text-sm font-medium shadow-none transition-colors">
                                    {isPlacingOrder || isPlaceOrderPending ? 'Processing...' : 'Proceed to Pay'}
                                </Button>
                            </div>
                        </div>

                        <UITarget targetId="sfcc.checkout.mainContent.after" />
                    </div>
                </div>
            </div>
            <UITarget targetId="sfcc.checkout.page.after" />
            {isRegistrationInProgress && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/60">
                    <Spinner size="lg" />
                </div>
            )}
        </div>
    );
}
